import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCompany, getCompanyCoupons, registerCouponUsage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Phone, Clock, Globe, Building2, Tag, Ticket, X, CheckCircle, Mail } from 'lucide-react';
import QRCode from 'react-qr-code';

const CompanyDetail = () => {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [usingCoupon, setUsingCoupon] = useState(false);
  const [couponUsed, setCouponUsed] = useState(false);

  // Get employee's company info from localStorage
  const employeeAccess = JSON.parse(localStorage.getItem('employeeAccess') || '{}');

  useEffect(() => {
    loadCompanyData();
  }, [id]);

  const loadCompanyData = async () => {
    try {
      const [companyRes, couponsRes] = await Promise.all([
        getCompany(id),
        getCompanyCoupons(id)
      ]);
      setCompany(companyRes.data);
      setCoupons(couponsRes.data);
    } catch (error) {
      console.error('Error loading company:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseCoupon = async () => {
    if (!selectedCoupon || !employeeAccess.company_id) return;
    
    setUsingCoupon(true);
    try {
      await registerCouponUsage(
        selectedCoupon.id,
        employeeAccess.company_id,
        employeeAccess.company_name || 'Empresa'
      );
      setCouponUsed(true);
    } catch (error) {
      console.error('Error using coupon:', error);
    } finally {
      setUsingCoupon(false);
    }
  };

  const closeModal = () => {
    setSelectedCoupon(null);
    setCouponUsed(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center">
        <Building2 className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Empresa no encontrada</h2>
        <Link to="/ofertas" className="text-[#d1223b] hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link 
              to="/ofertas" 
              className="flex items-center gap-2 text-[#64748B] hover:text-[#d1223b] transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Volver</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Company Header */}
      <section className="hero-gradient text-white py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden bg-white p-2 shadow-lg">
              {company.logo_url ? (
                <img 
                  src={company.logo_url} 
                  alt={company.name}
                  className="w-full h-full object-contain"
                  onError={function(e) {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/200x200/f1f5f9/d1223b?text=' + encodeURIComponent(company.name.substring(0, 2));
                  }}
                />
              ) : (
                <div className="w-full h-full bg-[#d1223b]/10 rounded-xl flex items-center justify-center">
                  <span className="text-3xl font-bold text-[#d1223b]">{company.name.substring(0, 2)}</span>
                </div>
              )}
            </div>
            <div className="text-center md:text-left">
              <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                <h1 className="text-2xl md:text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="company-name">
                  {company.name}
                </h1>
              </div>
              {company.category && (
                <Badge variant="secondary" className="bg-white/20 text-white border-0">
                  {company.category}
                </Badge>
              )}
              {company.description && (
                <p className="text-white/80 mt-3 max-w-2xl">{company.description}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Company Info */}
          <aside className="lg:col-span-4">
            <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm sticky top-24">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Información de Contacto
              </h3>
              <div className="space-y-4">
                {company.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#d1223b] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-[#64748B]">Dirección</p>
                      <p className="text-[#0F172A]" data-testid="company-address">{company.address}</p>
                    </div>
                  </div>
                )}
                {company.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-[#d1223b] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-[#64748B]">Teléfono</p>
                      <a href={'tel:' + company.phone} className="text-[#d1223b] hover:underline" data-testid="company-phone">
                        {company.phone}
                      </a>
                    </div>
                  </div>
                )}
                {company.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-[#d1223b] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-[#64748B]">Email</p>
                      <a href={'mailto:' + company.email} className="text-[#d1223b] hover:underline" data-testid="company-email">
                        {company.email}
                      </a>
                    </div>
                  </div>
                )}
                {company.hours && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#d1223b] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-[#64748B]">Horario</p>
                      <p className="text-[#0F172A]" data-testid="company-hours">{company.hours}</p>
                    </div>
                  </div>
                )}
                {company.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-[#d1223b] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-[#64748B]">Web</p>
                      <a 
                        href={company.website.startsWith('http') ? company.website : 'https://' + company.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#d1223b] hover:underline"
                        data-testid="company-website"
                      >
                        Visitar sitio web
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Coupons */}
          <section className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <Ticket className="w-6 h-6 text-[#fcd605]" />
              <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Cupones Disponibles
              </h3>
              <Badge variant="secondary" className="bg-[#d1223b]/10 text-[#d1223b]">
                {coupons.filter(function(c) { return c.is_active; }).length}
              </Badge>
            </div>

            {coupons.length === 0 ? (
              <div className="bg-white rounded-xl p-12 border border-slate-100 text-center">
                <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-[#0F172A] mb-2">Sin cupones disponibles</h4>
                <p className="text-[#64748B]">Esta empresa aún no ha publicado cupones de descuento</p>
              </div>
            ) : (
              <div className="space-y-4">
                {coupons.filter(function(c) { return c.is_active; }).map(function(coupon, index) {
                  return (
                    <CouponCard 
                      key={coupon.id} 
                      coupon={coupon} 
                      index={index}
                      onView={function() { setSelectedCoupon(coupon); }}
                    />
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Simple Modal - Compatible with all browsers */}
      {selectedCoupon && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-xl max-w-md w-full p-6 relative"
            onClick={function(e) { e.stopPropagation(); }}
          >
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100"
              data-testid="close-modal"
            >
              <X className="w-5 h-5 text-[#64748B]" />
            </button>
            
            <h2 className="text-xl font-bold text-center mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedCoupon.title}
            </h2>
            
            <div className="flex flex-col items-center">
              {couponUsed ? (
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-emerald-600 mb-4">¡Cupón Activado!</h3>
                  
                  {/* Show coupon image if available */}
                  {selectedCoupon.barcode_image_url ? (
                    <div className="mb-4 rounded-xl overflow-hidden border-2 border-emerald-200">
                      <img 
                        src={selectedCoupon.barcode_image_url} 
                        alt={selectedCoupon.title}
                        className="w-full max-w-sm mx-auto object-contain"
                      />
                    </div>
                  ) : selectedCoupon.qr_data ? (
                    <div className="bg-white p-4 rounded-xl border-2 border-emerald-200 mb-4 inline-block">
                      <QRCode 
                        value={selectedCoupon.qr_data}
                        size={180}
                        level="H"
                      />
                    </div>
                  ) : null}
                  
                  {/* Clickable link if available */}
                  {selectedCoupon.link_url && (
                    <a 
                      href={selectedCoupon.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-[#d1223b] text-white text-center font-bold py-3 px-6 rounded-lg hover:bg-[#b91c33] transition-colors mb-4"
                      data-testid="coupon-link"
                    >
                      {selectedCoupon.link_text || 'Haz click aquí para acceder al descuento'}
                    </a>
                  )}

                  {!selectedCoupon.link_url && (
                    <p className="text-sm text-[#64748B] mb-2">Muestra este cupón al establecimiento</p>
                  )}
                  {!selectedCoupon.barcode_image_url && selectedCoupon.code && (
                    <div className="bg-emerald-50 px-6 py-3 rounded-lg inline-block">
                      <span className="font-mono font-bold text-emerald-700 text-2xl">
                        {selectedCoupon.code}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Coupon image, QR, or code - Blurred until used */}
                  <div className="bg-white p-4 rounded-xl border-2 border-dashed border-slate-200 mb-6 relative">
                    {selectedCoupon.barcode_image_url ? (
                      <div style={{ filter: 'blur(8px)' }}>
                        <img 
                          src={selectedCoupon.barcode_image_url} 
                          alt={selectedCoupon.title}
                          className="w-full max-w-sm mx-auto object-contain"
                        />
                      </div>
                    ) : selectedCoupon.qr_data ? (
                      <div style={{ filter: 'blur(8px)' }}>
                        <QRCode 
                          value={selectedCoupon.qr_data}
                          size={180}
                          level="H"
                        />
                      </div>
                    ) : (
                      <div className="py-6 text-center" style={{ filter: 'blur(6px)' }}>
                        <span className="font-mono font-bold text-3xl text-[#0F172A]">
                          {selectedCoupon.code || '••••••'}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-[#d1223b] text-white text-xs font-bold px-3 py-1 rounded-full">
                        Pulsa "Usar Cupón" para ver
                      </span>
                    </div>
                  </div>
                  
                  {/* Coupon Details - hidden when coupon has image */}
                  {!selectedCoupon.barcode_image_url && (
                    <>
                      {selectedCoupon.discount && (
                        <div className="text-3xl font-bold text-[#d1223b] mb-2">
                          {selectedCoupon.discount}
                        </div>
                      )}
                      {selectedCoupon.code && (
                        <div className="bg-slate-100 px-4 py-2 rounded-lg mb-4 relative">
                          <span className="text-sm text-[#64748B]">Código: </span>
                          <span 
                            className="font-mono font-bold text-[#0F172A]" 
                            style={{ filter: 'blur(4px)' }}
                            data-testid="coupon-code"
                          >
                            {selectedCoupon.code}
                          </span>
                        </div>
                      )}
                      {selectedCoupon.description && (
                        <p className="text-center text-[#64748B] mb-4">{selectedCoupon.description}</p>
                      )}
                    </>
                  )}
                  {selectedCoupon.expires_at && (
                    <p className="text-sm text-[#64748B] mb-4">
                      Válido hasta: {new Date(selectedCoupon.expires_at).toLocaleDateString('es-ES')}
                    </p>
                  )}
                  
                  {/* Use Coupon Button */}
                  <Button 
                    className="w-full btn-primary mt-2"
                    onClick={handleUseCoupon}
                    disabled={usingCoupon}
                    data-testid="use-coupon-btn"
                  >
                    {usingCoupon ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Registrando...
                      </span>
                    ) : (
                      'Usar Cupón'
                    )}
                  </Button>
                  <p className="text-xs text-[#64748B] mt-2 text-center">
                    Pulsa cuando vayas a canjear el cupón
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_619dfbd9-e9ac-4905-ae17-ad2e8903622d/artifacts/tnur302o_Firma%2020%20Aniversario%20%28251%20x%2088%20px%29%20%28350%20x%20100%20px%29%20%281%29%20%282%29%20%281%29.png" 
                  alt="Cámara Lanzarote" 
                  className="h-8 w-auto"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_619dfbd9-e9ac-4905-ae17-ad2e8903622d/artifacts/ibo9k545_LOGO%20CLUB%20CA%CC%81MARA%20%2B%20SIN%20FONDO.png" 
                  alt="Club Cámara" 
                  className="h-10 w-auto"
                />
              </div>
            </div>
            <p className="text-slate-400 text-sm">
              © 2026 Cámara de Comercio de Lanzarote y La Graciosa
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

var CouponCard = function(props) {
  var coupon = props.coupon;
  var index = props.index;
  var onView = props.onView;
  
  return (
    <div 
      className="coupon-ticket p-6 stagger-item cursor-pointer hover:border-[#d1223b]/30 transition-colors"
      style={{ animationDelay: (index * 100) + 'ms' }}
      onClick={onView}
      data-testid={'coupon-card-' + coupon.id}
    >
      <div className="flex flex-col md:flex-row items-center gap-6 w-full">
        {/* Discount Badge */}
        <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-gradient-to-br from-[#d1223b] to-[#8a1627] flex items-center justify-center">
          <span className="text-white text-xl font-bold text-center leading-tight">
            {coupon.discount || 'Oferta'}
          </span>
        </div>

        {/* Content */}
        <div className="flex-grow text-center md:text-left">
          <h4 className="text-lg font-semibold text-[#0F172A] mb-1">{coupon.title}</h4>
          {coupon.description && (
            <p className="text-[#64748B] text-sm">{coupon.description}</p>
          )}
        </div>

        {/* Action */}
        <div className="flex-shrink-0">
          <Button className="btn-primary" data-testid={'view-coupon-' + coupon.id}>
            Ver Cupón
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;
